import React from "react";
import { Stack, Paper } from "@mui/material";
import { useTranslation } from "next-i18next";
import { ICertificateDict } from "src/api/server/wf-detail.types";
import { EmptyState } from "src/components-mui/common/empty-state/empty-state";
import { ErrorBoundary } from "src/components-mui/common/error-boundary/error-boundary";
import { CERTIFICATE_BOX_TEST_ID } from "src/components-mui/pages/detail/certificate-box/certificate-box.test-ids";
import { CertificateBoxProvider, ICertificateBoxData, useCertificateBoxContext } from "src/components-mui/pages/detail/certificate-box/context";
import { useCertificate } from "src/components-mui/pages/detail/certificate-box/hooks/use-certificate";
import { CertificateBoxBarred } from "src/components-mui/pages/detail/certificate-box/variants/barred";
import { CertificateBoxClosed } from "src/components-mui/pages/detail/certificate-box/variants/closed";
import { CertificateBoxClosingInProgress } from "src/components-mui/pages/detail/certificate-box/variants/closing-in-progress";
import { CertificateBoxTest } from "src/components-mui/pages/detail/certificate-box/variants/in-test";
import { CertificateBoxInvestable } from "src/components-mui/pages/detail/certificate-box/variants/investable";
import { CertificateBoxNotLicensedAll } from "src/components-mui/pages/detail/certificate-box/variants/not-licensed-all";
import { CertificateBoxNotLicensedSingle } from "src/components-mui/pages/detail/certificate-box/variants/not-licensed-single";
import { CertificateBoxPublished } from "src/components-mui/pages/detail/certificate-box/variants/published";
import { EnumWikifolioStatus } from "src/types/common-enum";

export interface ICertificateBoxProps {
    dict: ICertificateDict;
    data: ICertificateBoxData;
}

const CertificateBoxContent = ({ data }: ICertificateBoxProps) => {
    const { selectedCertificate } = useCertificateBoxContext();

    switch (data.wikifolio.status) {
        case EnumWikifolioStatus.Investable:
            const notLicensedCertificates = data.certificates.filter(certificate => !certificate.isLicensed);

            if (notLicensedCertificates.length === data.certificates.length) {
                return <CertificateBoxNotLicensedAll />;
            }

            return selectedCertificate.isLicensed ? <CertificateBoxInvestable /> : <CertificateBoxNotLicensedSingle />;
        case EnumWikifolioStatus.ClosingInProgress:
            return <CertificateBoxClosingInProgress />;
        case EnumWikifolioStatus.Barred:
            return <CertificateBoxBarred />;
        case EnumWikifolioStatus.Closed:
            return <CertificateBoxClosed />;
        case EnumWikifolioStatus.Test:
        case EnumWikifolioStatus.PublishingRequested:
            return <CertificateBoxTest />;
        case EnumWikifolioStatus.Published:
        case EnumWikifolioStatus.IssuingRequested:
        case EnumWikifolioStatus.EmissionProcessStarted:
        case EnumWikifolioStatus.ReadyForIssuing:
            return <CertificateBoxPublished />;
        default:
            return null;
    }
};

export const CertificateBox = (props: ICertificateBoxProps) => {
    const { selectedCertificate, updateSelectedCertificate } = useCertificate(props.data.certificates);

    const { t } = useTranslation("common");

    return (
        <React.StrictMode>
            <ErrorBoundary
                FallbackComponent={() => (
                    <EmptyState
                        title={t("errors.messages.generic.heading")}
                        description={t("errors.messages.generic.message")}
                        boxShadow={undefined}
                    />
                )}
            >
                <Paper
                    component="section"
                    elevation={3}
                    sx={{
                        height: "auto",
                        width: "100%",
                        overflow: "hidden",
                        mx: "auto",
                    }}
                    data-test-id={CERTIFICATE_BOX_TEST_ID}
                >
                    <Stack spacing={2}>
                        <CertificateBoxProvider
                            value={{
                                dict: props.dict,
                                data: props.data,
                                selectedCertificate,
                                updateSelectedCertificate,
                            }}
                        >
                            <CertificateBoxContent {...props} />
                        </CertificateBoxProvider>
                    </Stack>
                </Paper>
            </ErrorBoundary>
        </React.StrictMode>
    );
};
